import React, { useContext } from "react";
import Grid from "@mui/material/Grid2";
import {
  ActionTypeEnum,
  ChasableDetailsModel,
  InvoiceStatus,
  actionEnumToLabelMap,
  EmailTemplateResponse,
} from "@attelas/shared";
import { DraftRecipientMode, hasOtherContacts } from "../../../utils/contactRecipients";
import { ChasablesContext } from "../../../context/ChasablesContext";
import { ToastContext } from "../../../context/ToastContext";
import { useOutlook } from "../../../hooks/useOutlook";
import { ChaseButton } from "../../../components/ChaseButton";
import { CompleteButton } from "../../../components/CompleteButton";
import { Footer } from "../../../components/Footer";
import { BackButton } from "../../../components/BackButton";
import { useSearchParams } from "react-router-dom";
import SkipButton from "../../../components/SkipButton/SkipButton";

export type SuggestedChasableFooterProps = {
  chasable: ChasableDetailsModel;
  hasBeenChased: boolean;
  isLoading: boolean;
  setIsLoading(loading: boolean): void;
  handleChaseChasable(languageId?: string, recipientMode?: DraftRecipientMode): void;
  handleDraftFromTemplate?(template: EmailTemplateResponse, recipientMode?: DraftRecipientMode): void;
  navigateToNextChasable(): void;
  onOpenAttachPickerForDraft?(anchorEl: HTMLElement): void;
  onOpenAdjustTone?(anchorEl: HTMLElement): void;
  onAddOtherContactsToCc?(): void;
};

export function SuggestedChasableFooter({
  chasable,
  hasBeenChased,
  isLoading,
  setIsLoading,
  handleChaseChasable,
  handleDraftFromTemplate,
  navigateToNextChasable,
  onOpenAttachPickerForDraft,
  onOpenAdjustTone,
  onAddOtherContactsToCc,
}: SuggestedChasableFooterProps) {
  const [searchParams] = useSearchParams();
  const showBackButton = !!searchParams.get("showBackButton");
  const { showToast } = useContext(ToastContext);
  const { isInComposeMode, isInReadMode } = useOutlook();
  const { chasables, completeChasable } = useContext(ChasablesContext);
  const cannotBeChased = !chasable.chasableId;
  const actions =
    chasables?.actions.filter(
      ({ type }) =>
        type === ActionTypeEnum.Chasable ||
        type === ActionTypeEnum.NewInvoice ||
        type === ActionTypeEnum.PromiseToPayBroken
    ) ?? [];

  const checkActionsAreCompleted = (): boolean => {
    if (!chasable) return false;
    const currentAction = actions.find(
      (action) => action.id === chasable.invoiceDetails.id || action.externalId === chasable.invoiceDetails.id
    );
    if (!currentAction) return false;
    const allCompleted =
      actions.filter((action) => action.id !== currentAction.id).filter((action) => action.type === currentAction.type)
        .length === 0;

    if (allCompleted) {
      const message = `All new ${actionEnumToLabelMap[currentAction.type]} are completed.`;
      showToast(message, "success");
      return true;
    }
    return false;
  };

  const handleSkipMenuItemClicked = async (numberOfDays: number) => {
    if (!chasable) return;

    try {
      const allCompleted = checkActionsAreCompleted();
      if (!allCompleted) {
        showToast(
          `The invoice ${chasable.invoiceDetails.invoiceNumber} has been skipped for ${numberOfDays} days`,
          "success"
        );
      }
      navigateToNextChasable();
    } catch {
      showToast("The skipping of the invoice failed, please try again", "error");
    }
  };

  const handleMarkAsComplete = async () => {
    if (!chasable) return;

    setIsLoading(true);
    try {
      await completeChasable(chasable.invoiceDetails.id);
      const allCompleted = checkActionsAreCompleted();
      if (!allCompleted) {
        showToast(`The invoice ${chasable.invoiceDetails.invoiceNumber} has been marked as completed`, "success");
      }
      navigateToNextChasable();
    } catch {
      showToast("The completion of the invoice failed, please try again", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (chasable.invoiceDetails.status === InvoiceStatus.PAID || chasable.invoiceDetails.status === InvoiceStatus.DRAFT) {
    if (!showBackButton) return null;
    return (
      <Footer>
        <BackButton />
      </Footer>
    );
  }

  return (
    <Footer>
      {showBackButton && (
        <Grid size={12}>
          <BackButton />
        </Grid>
      )}
      {!cannotBeChased && (
        <>
          <Grid size={6} container>
            <SkipButton
              handleSkipMenuItemClicked={handleSkipMenuItemClicked}
              handleMarkAsComplete={!hasBeenChased && !isInReadMode ? handleMarkAsComplete : undefined}
              isLoading={isLoading}
              invoicesNumber={[chasable.invoiceDetails.id]}
            />
          </Grid>
          <Grid size={6}>
            {isInComposeMode ? (
              hasBeenChased ? (
                <CompleteButton
                  onClick={handleMarkAsComplete}
                  loading={isLoading}
                  disabled={isLoading}
                  onOpenAdjustTone={onOpenAdjustTone}
                  onAddOtherContactsToCc={
                    onAddOtherContactsToCc && hasOtherContacts(chasable.customerDetails.contacts)
                      ? onAddOtherContactsToCc
                      : undefined
                  }
                />
              ) : (
                <ChaseButton
                  onClick={handleChaseChasable}
                  loading={isLoading}
                  disabled={isLoading}
                  contacts={chasable.customerDetails.contacts}
                  invoiceCount={1}
                  onOpenAttachPickerForDraft={onOpenAttachPickerForDraft}
                  onEmailTemplateClick={handleDraftFromTemplate}
                />
              )
            ) : (
              <CompleteButton onClick={handleMarkAsComplete} loading={isLoading} disabled={isLoading} />
            )}
          </Grid>
        </>
      )}
    </Footer>
  );
}
